<script setup>
import IconCheck from 'assets/images/svg/check.svg';
import Logo from 'assets/images/svg/logo.svg';
import Phone from 'assets/images/svg/phone.svg';
import {ref, onMounted, onUnmounted} from 'vue';

const contacts = await queryContent('/contacts').findOne();

const activeCardId = ref(null);

const setActiveCard = (cardId) => {
  activeCardId.value = cardId;
}

const cardRefs = ref([]);

onMounted(() => {
  cardRefs.value[0].click();
})

const isWithinRange = ref(false);

const updateRangeStatus = () => {
  const width = window.innerWidth;
  isWithinRange.value = width >= 1280 && width <= 1729;
};

onMounted(() => {
  updateRangeStatus();
  window.addEventListener("resize", updateRangeStatus);
});

onUnmounted(() => {
  window.removeEventListener("resize", updateRangeStatus);
});
</script>

<template>
  <section id="contacts-section" class="contacts">
    <div class="container">
      <div class="contacts__top">
        <h2 class="contacts__title text-40">{{ contacts.title }}</h2>
        <Logo class="contacts__logo-tablet" />
      </div>
      <div class="contacts__row">
        <div class="contacts__header">
          <Logo class="contacts__logo" />
          <ul class="contacts__features">
            <li v-for="(item, index) in contacts.listText" :key="index" class="contacts__feature">
              <IconCheck class="contacts__check-icon" />
              <span class="text-18">
                {{ item.text }}
              </span>
            </li>
          </ul>
        </div>
        <div class="contacts__list">
          <div
            v-for="(item, index) in contacts.listCards"
            :key="index"
            ref="cardRefs"
            :class="`contacts__person
            ${index + 1 === activeCardId ? 'contacts__person--active' : ''}
            ${isWithinRange ? 'contacts__person--active' : ''}
            `"
            @click="setActiveCard(index + 1)"
          >
            <div class="contacts__person-top">
              <p class="contacts__name text-24" v-html="item.name" />
              <NuxtImg :src="item.photo"
                       class="contacts__photo"
                       loading="lazy"
                       format="webp"
                       :alt="`Фото спикера ${item.name}`"
              />
              <div class="contacts__person-responsibility text-32">
                {{ item.responsibility }}
              </div>
            </div>
            <div class="contacts__info">
              <p class="contacts__role text-20">{{ item.role }}</p>
              <div class="contacts__info-inner">
                <div class="contacts__icon-wrapper">
                  <Phone class="contacts__phone-icon" />
                </div>
                <div>
                  <a :href="`tel:${item.phone}`" class="contacts__phone text-32">
                    <span class="contacts__phone-text">
                      {{ item.phone }}
                    </span>
                  </a>
                  <a :href="`mailto:${item.email}`" class="contacts__email text-20">
                    {{ item.email }}
                  </a>
                </div>
              </div>
            </div>
            <NuxtImg
              class="contacts__bg-logo"
              alt="Логотип"
              src="/img/ContactsOrganizers/bg-logo.png"
              loading="lazy"
              format="webp"
            />
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped lang="scss">
.contacts {
  background-color: var(--bg-grey);
  padding: 68px 0;

  @media (max-width: $large) {
    padding: 56px 0;
  }

  @media (max-width: $medium) {
    padding: 40px 0;
  }

  &__phone-icon {
    fill: var(--success-color);
  }

  &__top {
    display: flex;
    align-items: center;
    justify-content: space-between;

    @media (max-width: $large) {
      margin-bottom: 56px;
    }

    @media (max-width: $medium) {
      margin-bottom: 36px;
    }

    @media (max-width: $mobile) {
      flex-direction: column;
      align-items: flex-start;
      gap: 36px;
    }
  }

  &__logo {
    margin-bottom: 52px;

    @media (max-width: $large) {
      display: none;
    }

    &-tablet {
      width: 240px;
      display: none;

      @media (max-width: $large) {
        display: block;
      }

      @media (max-width: $medium) {
        width: 212px;
      }

      @media (max-width: $mobile) {
        width: 200px;
      }
    }
  }

  &__info {
    display: none;

    &-inner {
      display: flex;
      gap: 20px;

      @media (max-width: $mobile) {
        gap: 14px;
      }
    }
  }

  &__features {
    @media (max-width: $large) {
      display: grid;
      grid-template-columns: 1fr 1fr;
    }

    @media (max-width: $medium) {
      grid-template-columns: 1fr;
    }
  }

  &__icon-wrapper {
    flex: 0 0 40px;
    height: 40px;
    background: white;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;

    @media (max-width: $mobile) {
      flex: 0 0 36px;
      height: 36px;
    }
  }

  &__name {
    display: none;
    color: white;
    position: relative;
    top: -22px;

    @media (max-width: $large) {
      top: 0;
    }

    @media (min-width: $mobile) {
      max-width: 100px;
    }
  }

  &__role {
    color: white;
    margin-bottom: 22px;

    @media (max-width: $mobile) {
      margin-bottom: 4px;
    }
  }

  &__phone {
    color: white;
    display: flex;
    align-items: center;
    gap: 20px;
  }

  &__row {
    display: grid;
    grid-template-columns: 1.9fr 3fr;
    gap: 87px;

    @media (max-width: $large) {
      grid-template-columns: 1fr;
      gap: 56px;
    }

    @media (max-width: $medium) {
      gap: 36px;
    }
  }

  &__photo {
    width: 128px;
    height: 128px;

    @media (max-width: $large) {
      width: 96px;
      height: 96px;
    }

    @media (max-width: $medium) {
      width: 72px;
      height: 72px;
    }
  }

  &__check-icon {
    flex: 0 0 40px;
    height: 40px;
    stroke: var(--primary-color);

    @media (max-width: $mobile) {
      flex: 0 0 36px;
      position: relative;
      top: 2px;
    }
  }

  &__title {
    color: var(--dark-color);
    margin-bottom: 68px;

    @media (max-width: $large) {
      margin-bottom: 0;
    }
  }

  &__feature {
    font-size: 16px;
    margin-bottom: 10px;
    display: flex;
    align-items: center;
    gap: 32px;

    &:not(:last-child) {
      margin-bottom: 36px;
    }

    @media (max-width: $mobile) {
      gap: 20px;
      align-items: flex-start;
    }
  }

  &__email {
    color: white;

    @media (max-width: $mobile) {
      position: relative;
      left: -50px;
      margin-top: 10px;
      display: block;
    }
  }

  &__list {
    display: flex;
    gap: 15px;

    @media (max-width: $mobile) {
      flex-direction: column;
    }
  }

  &__person-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    position: relative;
  }

  &__bg-logo {
    position: absolute;
    left: 0;
    bottom: 29px;
    width: 90%;
    object-fit: contain;
    display: none;
    pointer-events: none;

    @media (max-width: $medium) {
      width: 60%;
    }
  }

  &__person {
    position: relative;
    padding: 37px 36px 66px 36px;
    border-radius: 20px;
    background-color: #d9dee6;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    min-height: 527px;
    flex: 0 0 200px;
    cursor: pointer;

    @media (max-width: $large) {
      min-height: 357px;
    }

    @media (max-width: $medium) {
      flex: 0 0 138px;
      padding: 16px 24px 33px;
      min-height: 320px;
    }

    @media (max-width: $mobile) {
      min-height: 204px;
    }

    &-responsibility {
      transform: rotate(-90deg);
      transform-origin: left bottom;
      position: absolute;
      left: 34px;
      bottom: -4px;

      @media (max-width: $medium) {
        font-size: 20px;
        left: 58px;
      }

      @media (max-width: $mobile) {
        transform: rotate(0deg);
        position: relative;
        left: auto;
      }
    }

    &:not(.contacts__person--active) {
      .contacts__person-top {
        flex-direction: column;
        justify-content: space-between;
        align-items: flex-start;
        height: 100%;

        @media (max-width: $medium) {
          align-items: center;
        }

        @media (max-width: $mobile) {
          flex-direction: row-reverse;
        }
      }

      @media (max-width: $mobile) {
        min-height: 104px;
      }
    }

    &--active {
      background: linear-gradient(315deg, #75CCC2 0%, #749BFF 50%, #7F74FF 100%);
      padding: 37px 40px 54px 72px;
      flex: 0 1 100%;

      @media (max-width: $large) {
        min-width: auto;
        padding: 40px;
      }

      @media (max-width: $medium) {
        padding: 16px 28px 18px;
      }

      .contacts__photo {
        width: 160px;
        height: 160px;

        @media (max-width: $large) {
          width: 96px;
          height: 96px;
        }

        @media (max-width: $medium) {
          width: 72px;
          height: 72px;
        }
      }

      .contacts__bg-logo {
        display: block;
      }

      .contacts__person-responsibility {
        display: none;
      }

      .contacts__info {
        display: block;
      }

      .contacts__name {
        display: block;
      }
    }
  }
}
</style>
